
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import Sidebar from '../components/Sidebar'
import ModuleHeader from '../components/module/ModuleHeader'
import LessonPlayer from '../components/module/LessonPlayer'
import LessonList from '../components/module/LessonList'
import QuizInterface from '../components/QuizInterface'
import { useModuleData } from '../hooks/useModuleData'
import { useModuleProgress } from '../hooks/useModuleProgress'
import { useRecentModules } from '@/hooks/useRecentModules'

const ModuleDetail = () => {
  const { moduleId } = useParams()
  const [currentLesson, setCurrentLesson] = useState<string>('')
  const [completionStats, setCompletionStats] = useState({
    completed: 0,
    total: 0,
    percentage: 0,
    isFullyCompleted: false
  })
  
  const { moduleData, loading } = useModuleData(moduleId)
  const { getModuleCompletionStats, isContentCompleted, markContentAsCompleted } = useModuleProgress(moduleId || '')
  const { addRecentModule } = useRecentModules()

  useEffect(() => {
    // Set first lesson as current if available
    if (moduleData?.content && moduleData.content.length > 0) {
      setCurrentLesson(moduleData.content[0].id)
    }
  }, [moduleData])

  // Update completion stats whenever module data or progress changes
  useEffect(() => {
    if (moduleData?.content) {
      const stats = getModuleCompletionStats(moduleData.content)
      setCompletionStats(stats)
    }
  }, [moduleData?.content, getModuleCompletionStats])

  const getCurrentLesson = () => {
    return moduleData?.content.find(lesson => lesson.id === currentLesson)
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Link to="/learn" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Learn
          </Link>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading module...</p>
            </div>
          ) : moduleData ? (
            <>
              <ModuleHeader 
                title={moduleData.title} 
                description={moduleData.description}
                category={moduleData.category}
                coins={moduleData.coins || 0}
                contentLength={moduleData.content?.length || 0}
                progress={completionStats.percentage}
              />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                <div className="lg:col-span-2">
                  <LessonPlayer 
                    currentLesson={getCurrentLesson()} 
                    moduleId={moduleId}
                    moduleTitle={moduleData.title}
                    moduleDescription={moduleData.description}
                    moduleCategory={moduleData.category}
                    isContentCompleted={isContentCompleted}
                    markContentAsCompleted={markContentAsCompleted}
                  />
                </div>
                <div>
                  <LessonList 
                    lessons={moduleData.content} 
                    currentLessonId={currentLesson}
                    onLessonSelect={setCurrentLesson}
                    moduleId={moduleId}
                    isContentCompleted={isContentCompleted}
                  />
                  <QuizInterface 
                    moduleId={moduleId} 
                    moduleTitle={moduleData.title}
                    isModuleCompleted={completionStats.isFullyCompleted}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">Module not found</p>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default ModuleDetail
